export interface AvailabilityFeedState {
  /** The availability request is in flight — first read or refetch. */
  isFetching: boolean;
  /**
   * react-query is serving `keepPreviousData`, so the answer on screen belongs
   * to an earlier month / guest mix. Omit for feeds that don't keep previous
   * data; they simply have no answer while fetching.
   */
  isPlaceholderData?: boolean;
  /** Some answer is on screen. */
  hasData: boolean;
}

export interface AvailabilityLoadingState {
  /**
   * No answer for the window on screen. Callers must not paint a day grid from
   * this state: an empty feed means opposite things to the two calendars —
   * per-category availability reads every date as blocked, package and
   * site-wide availability read every date as open.
   */
  isStale: boolean;
  /**
   * Refetching an answer that does match the window on screen. The grid is
   * still correct, so it stays live and only takes a progress hint.
   */
  isRefreshing: boolean;
}

/**
 * Split "availability is loading" into the two cases that need different
 * treatment on screen. Shared so the calendars can't drift apart on which one
 * they call which.
 */
export const getAvailabilityLoadingState = ({
  isFetching,
  isPlaceholderData = false,
  hasData,
}: AvailabilityFeedState): AvailabilityLoadingState => {
  const isStale = isPlaceholderData || (isFetching && !hasData);
  return { isStale, isRefreshing: isFetching && !isStale };
};
